import * as React from 'react';
import { connect } from 'react-redux';
import { actions } from 'react-redux-form';
import * as moment from 'moment';
import * as validator from 'validator';
import { merge } from 'lodash';
import * as deepEqual from 'deep-equal';
import { Button, Card, IconButton, Form, Notification } from '../';
import {
  updateCompany,
  showError,
  clearErrors,
} from '../../redux/actions/companies';
import ACCOUNT_TYPES from '../../data/accountTypes';
import CHECKBOX from '../../data/checkboxSelect';
import FIELDS from '../../data/fields';
import AddressPicker from '../AddressPicker/AddressPicker';

interface Props {
  company: any;
  handleUpdate: Function;
  handleBack?: Function;
  isDisabled?: boolean;
  isLoading: boolean;
  didInvalidate: boolean;
  errorMessage: string | boolean;
  dispatch: Function;
  updateButtonText?: string;
  backButtonText?: string;
  isSectioned?: boolean;
  onErrors?: (errors) => void;
}

interface State {
  formData: any;
  initialFormData: any;
  section: number;
  errors: string[];
}

class CompanyUpdateForm extends React.Component<Props, State> {
  private readyCount: number;

  constructor(props) {
    super(props);
    this.state = {
      formData: {},
      initialFormData: null,
      section: 0,
      errors: [],
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.checkForReady = this.checkForReady.bind(this);
    this.isChanged = this.isChanged.bind(this);

    // This is used to detect if the form has been initialised
    this.readyCount = 0;
  }

  /**
   * Check if all the forms are ready. If they are store the
   * form data as the initial state.
   */
  checkForReady() {
    if (this.state.initialFormData !== null) {
      return;
    }
    this.readyCount++;
    // Once all the forms have been initialised the initial formData can be stored to
    // check for changes
    if (this.readyCount === 6) {
      this.setState({ initialFormData: merge({}, this.state.formData) });
    }
  }

  /**
   * Return whether the form has changed
   */
  isChanged() {
    return !deepEqual(this.state.formData, this.state.initialFormData);
  }

  /**
   * Handler for storing form data changes
   * @param {Object} data
   */
  handleChange(data) {
    this.setState(
      { formData: merge(this.state.formData, data) },
      this.checkForReady,
    );
  }

  /**
   * Handling for data validation before submission
   * @param {Object} data
   */
  handleValidate(data) {
    if (this.props.didInvalidate) {
      this.props.dispatch(clearErrors());
    }
    if (this.props.onErrors) {
      this.props.onErrors([]);
    }

    const errorFields = [];
    const errorMessages = [];

    if (validator.isEmpty(data.name)) {
      errorFields.push('name');
      errorMessages.push('Please specify a name');
    }
    if (validator.isEmpty(String(data.phone_number))) {
      errorFields.push('phone_number');
      errorMessages.push('Please specify a phone number');
    }
    if (
      !validator.isEmpty(data.general_contact_email) &&
      !validator.isEmail(data.general_contact_email)
    ) {
      errorFields.push('general_contact_email');
      errorMessages.push(
        'Please specify a valid general contact email address',
      );
    }
    if (
      !validator.isEmpty(data.accounts_contact_email) &&
      !validator.isEmail(data.accounts_contact_email)
    ) {
      errorFields.push('accounts_contact_email');
      errorMessages.push(
        'Please specify a valid accounts contact email address',
      );
    }

    if (validator.isEmpty(data.postcode)) {
      errorFields.push('postcode');
      errorMessages.push('Please specify a post code');
    }

    if (validator.isEmpty(data.address)) {
      errorFields.push('address');
      errorMessages.push('Please specify the address');
    }

    if (
      !validator.isEmpty(data.expiry_date) &&
      validator.isBefore(data.expiry_date, new Date().toString())
    ) {
      errorFields.push('expiry_date');
      errorMessages.push("Please specify an expiry date that hasn't expired");
    }

    if (Number(data.has_account) === 1) {
      if (data.account_type === null) {
        errorFields.push('has_account');
        errorMessages.push('Please specify an account type');
      }
      if (
        !validator.isEmpty(data.account_card_expiry) &&
        !moment(data.account_card_expiry).isValid() &&
        data.account_type === 'END_OF_HIRE'
      ) {
        errorFields.push('account_card_expiry');
        errorMessages.push('Please specify a valid card expiry date');
      }
      if (validator.isBefore(data.account_card_expiry, new Date().toString())) {
        errorFields.push('account_card_expiry');
        errorMessages.push(
          "Please specify a card expiry date that hasn't expired",
        );
      }
    }

    if (errorMessages.length) {
      if (this.props.onErrors) {
        this.props.onErrors(errorMessages);
      } else {
        this.setState({ errors: errorFields });
        this.props.dispatch(showError(errorMessages.join('\n')));
      }
      return false;
    }

    return data;
  }

  /**
   * Handler for form submission
   * @return {Promise}
   */
  async handleSubmit() {
    if (!this.props.isLoading) {
      const data = this.handleValidate(this.state.formData);
      if (data === false) {
        return;
      }
      let company = this.props.company;
      const isChanged = this.isChanged();
      if (isChanged) {
        const response = await this.props.dispatch(
          updateCompany(this.props.company.id, data),
        );
        if (response.error) {
          this.props.dispatch(showError(response.error));
          return;
        }
        company = response.data;
      }
      this.props.handleUpdate(company, isChanged);
    }
  }

  render() {
    return (
      <div className={this.props.isSectioned ? 'is-sectioned' : ''}>
        <Card>
          {(!this.props.isSectioned || this.state.section === 0) && (
            <div>
              <Form
                fields={FIELDS.companies.updateGeneric}
                type="generic"
                handleChange={(data) => {
                  this.handleChange(data);
                }}
                handleSubmit={this.handleSubmit}
                errors={this.state.errors}
                getDispatch={(dispatch) => {
                  dispatch(
                    actions.change(
                      'generic.name',
                      this.state.formData.name || this.props.company.name || '',
                    ),
                  );
                  dispatch(
                    actions.change(
                      'generic.phone_number',
                      this.state.formData.phone_number ||
                        this.props.company.phone_number ||
                        '',
                    ),
                  );
                }}
                hasButton={false}
                disabled={this.props.isDisabled}
              />
              <AddressPicker
                postcode={
                  this.state.formData.postcode || this.props.company.postcode
                }
                errors={this.state.errors}
                address={{
                  address:
                    this.state.formData.address || this.props.company.address,
                  address_2:
                    this.state.formData.address_2 ||
                    this.props.company.address_2,
                  city: this.state.formData.city || this.props.company.city,
                  county:
                    this.state.formData.county || this.props.company.county,
                }}
                handleUpdate={(
                  postcode,
                  { address, address_2, city, county },
                ) => {
                  this.handleChange({
                    postcode,
                    address,
                    address_2,
                    city,
                    county,
                  });
                }}
              />
            </div>
          )}
          {(!this.props.isSectioned || this.state.section === 1) && (
            <Form
              fields={FIELDS.companies.updateContact}
              type="contact"
              handleChange={(data) => {
                this.handleChange(merge(this.state.formData, data));
              }}
              handleSubmit={this.handleSubmit}
              errors={this.state.errors}
              getDispatch={(dispatch) => {
                dispatch(
                  actions.change(
                    'contact.general_contact_name',
                    this.state.formData.general_contact_name ||
                      this.props.company.general_contact_name ||
                      '',
                  ),
                );
                dispatch(
                  actions.change(
                    'contact.general_contact_email',
                    this.state.formData.general_contact_email ||
                      this.props.company.general_contact_email ||
                      '',
                  ),
                );
                dispatch(
                  actions.change(
                    'contact.accounts_contact_name',
                    this.state.formData.accounts_contact_name ||
                      this.props.company.accounts_contact_name ||
                      '',
                  ),
                );
                dispatch(
                  actions.change(
                    'contact.accounts_contact_email',
                    this.state.formData.accounts_contact_email ||
                      this.props.company.accounts_contact_email ||
                      '',
                  ),
                );
              }}
              hasButton={false}
              disabled={this.props.isDisabled}
            />
          )}
          {(!this.props.isSectioned || this.state.section === 2) && (
            <Form
              fields={FIELDS.companies.updateInsurance}
              values={{
                pay_own_insurance: CHECKBOX,
              }}
              errors={this.state.errors}
              type="insurance"
              handleChange={(data) => {
                this.handleChange(merge(this.state.formData, data));
              }}
              handleSubmit={this.handleSubmit}
              getDispatch={(dispatch) => {
                const payOwnInsurance =
                  this.state.formData.pay_own_insurance ||
                  this.props.company.pay_own_insurance;
                dispatch(
                  actions.change(
                    'insurance.pay_own_insurance',
                    Number(payOwnInsurance),
                  ),
                );
                dispatch(
                  actions.change(
                    'insurance.insurer',
                    this.state.formData.insurer ||
                      this.props.company.insurer ||
                      '',
                  ),
                );
                dispatch(
                  actions.change(
                    'insurance.policy_number',
                    this.state.formData.policy_number ||
                      this.props.company.policy_number ||
                      '',
                  ),
                );
                const expiryDate =
                  this.state.formData.expiry_date ||
                  this.props.company.expiry_date;
                dispatch(
                  actions.change(
                    'insurance.expiry_date',
                    expiryDate ? expiryDate.substring(0, 10) : '',
                  ),
                );
              }}
              hasButton={false}
              disabled={this.props.isDisabled}
            />
          )}
          {(!this.props.isSectioned || this.state.section === 3) && (
            <Form
              fields={FIELDS.companies.updateAccount}
              values={{
                has_account: CHECKBOX,
                account_online_bypass: CHECKBOX,
                account_type: ACCOUNT_TYPES,
              }}
              type="account"
              errors={this.state.errors}
              handleChange={(data) => {
                this.handleChange(merge(this.state.formData, data));
              }}
              handleSubmit={this.handleSubmit}
              getDispatch={(dispatch) => {
                const hasAccount =
                  this.state.formData.has_account ||
                  this.props.company.has_account;
                dispatch(
                  actions.change('account.has_account', Number(hasAccount)),
                );
                dispatch(
                  actions.change(
                    'account.account_card_expiry',
                    moment(
                      this.state.formData.account_card_expiry ||
                        this.props.company.account_card_expiry ||
                        '',
                    ).format('YYYY-MM-DD'),
                  ),
                );
                dispatch(
                  actions.change(
                    'account.account_type',
                    this.state.formData.account_type ||
                      this.props.company.account_type,
                  ),
                );
                dispatch(
                  actions.change(
                    'account.account_online_bypass',
                    Number(
                      this.state.formData.account_online_bypass ||
                        this.props.company.account_online_bypass,
                    ),
                  ),
                );
              }}
              hasButton={false}
              disabled={this.props.isDisabled}
            />
          )}
          <div className="field" />
          {!this.props.isDisabled && (
            <div className="field is-clearfix">
              {(!this.props.isSectioned || this.state.section === 0) &&
                this.props.handleBack && (
                  <IconButton
                    className={`is-primary is-pulled-left${
                      this.props.isLoading ? ' is-loading' : ''
                    }`}
                    handleClick={this.props.handleBack}
                    icon="arrow-left"
                  >
                    {this.props.backButtonText}
                  </IconButton>
                )}
              {(!this.props.isSectioned || this.state.section === 3) && (
                <Button
                  className={`is-primary is-pulled-right\
                  ${this.props.isLoading ? ' is-loading' : ''}`}
                  handleClick={this.handleSubmit}
                >
                  {this.props.updateButtonText
                    ? this.props.updateButtonText
                    : 'Update'}
                </Button>
              )}
              {!!this.props.isSectioned &&
                this.state.section < 3 && (
                  <div>
                    <Button
                      className="is-primary is-outlined is-pulled-right"
                      handleClick={() => {
                        this.setState({ section: this.state.section + 1 });
                      }}
                    >
                      Next &raquo;
                    </Button>
                  </div>
                )}
              {!!this.props.isSectioned &&
                this.state.section > 0 &&
                !this.props.isLoading && (
                  <div>
                    <Button
                      className="is-default is-outlined"
                      handleClick={() => {
                        this.setState({ section: this.state.section - 1 });
                      }}
                    >
                      &laquo; Previous
                    </Button>
                  </div>
                )}
            </div>
          )}
        </Card>
        {!this.props.isLoading &&
          this.props.didInvalidate && (
            <Notification type="danger">{this.props.errorMessage}</Notification>
          )}
      </div>
    );
  }
}

const mapStateToProps = ({ companies }) => ({
  isLoading: companies.isFetching,
  didInvalidate: companies.didInvalidate,
  errorMessage: companies.error,
});

export default connect(mapStateToProps)(CompanyUpdateForm);
